import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    CircularProgress,
    Stack,
    Chip,
    alpha,
    Dialog,
    Button
} from '@mui/material';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import webSocketService from '../../../services/webSocketService';

// Иконки
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ComplexVideo = ({ host, hostType = 'default' }) => {
    const isDarkMode = useSelector(state => state.settings.darkMode);

    const [hosts, setHosts] = useState([]);
    const [selectedHost, setSelectedHost] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamId, setStreamId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [frame, setFrame] = useState(null);
    const [frameCount, setFrameCount] = useState(0);
    const [fps, setFps] = useState(0);
    const [viewers, setViewers] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);

    const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [isPanning, setIsPanning] = useState(false);
    const panStart = useRef({ x: 0, y: 0 });
    const containerRef = useRef(null);
    const frameCountRef = useRef(0);
    const lastFpsUpdateRef = useRef(Date.now());

    // Получаем список камер из дочерних хостов (аналогично ComplexPhotos)
    const availableHosts = useMemo(() => {
        const result = [];

        console.log('ComplexVideo: hostType=', hostType, 'host=', host);

        if ((hostType === 'crossroad' || hostType === 'vgk') && host?.child) {
            host.child.forEach(child => {
                // 1. RTSP камеры (axis, idis, apix)
                const typeTag = child.tags?.find(t => 
                    t.value === 'axis' || t.value === 'idis' || t.value === 'apix'
                );
                
                if (typeTag) {
                    const ip = child.interfaces?.find(i => i.type === '1' && i.ip?.includes('192.168.'))?.ip;
                    if (ip) {
                        result.push({
                            hostid: child.hostid,
                            name: child.name.split('/')[1] || child.name,
                            type: typeTag.value, // axis, idis или apix
                            ip
                        });
                        console.log('ComplexVideo: Найдена RTSP камера', typeTag.value, ip, child.name);
                    }
                }

                // 2. MJPEG камеры (VSC - видеосервер)
                const vscTag = child.tags?.find(t => t.value === 'vsc');
                if (vscTag) {
                    const ip = child.interfaces?.find(i => i.type === '1' && i.ip?.includes('192.168.'))?.ip;
                    if (ip) {
                        // Проверяем доступность камеры 245
                        if (child.items?.find(i => i.name === '10.245.0.1' && i.status === '0')) {
                            result.push({
                                hostid: `${child.hostid}/245`,
                                name: `${child.name.split('/')[1] || child.name} :: 245`,
                                type: 'mjpeg245',
                                ip
                            });
                            console.log('ComplexVideo: Найдена MJPEG камера 245', ip, child.name);
                        }
                        // Проверяем доступность камеры 246
                        if (child.items?.find(i => i.name === '10.246.0.1' && i.status === '0')) {
                            result.push({
                                hostid: `${child.hostid}/246`,
                                name: `${child.name.split('/')[1] || child.name} :: 246`,
                                type: 'mjpeg246',
                                ip
                            });
                            console.log('ComplexVideo: Найдена MJPEG камера 246', ip, child.name);
                        }
                    }
                }
            });
        }

        if (hostType === 'speed') {
            const type = host.tags.find(tag => tag.tag === 'type')?.value
            if (type === 'cyclop') {
                const ip = host.interfaces?.find(i => i.type === '1' && i.ip?.includes('192.168.'))?.ip;
                result.push({
                    hostid: `10.245.0.2`,
                    name: `10.245.0.2`,
                    type: 'cyclop',
                    ip
                })
            }
        }

        console.log('ComplexVideo: Найдено камер:', result.length);
        return result.sort((a, b) => a.name.localeCompare(b.name));
    }, [hostType, host]);

    // Инициализация списка хостов
    useEffect(() => {
        setHosts(availableHosts);
        if (availableHosts.length > 0 && !selectedHost) {
            setSelectedHost(availableHosts[0]);
        }
    }, [availableHosts, selectedHost]);

    // Храним предыдущий URL для очистки
    const prevFrameUrlRef = useRef(null);

    // Обработчик кадров от сервера
    const handleFrame = useCallback((data) => {
        if (data.streamId === streamId) {
            // Освобождаем предыдущий Blob URL
            if (prevFrameUrlRef.current) {
                URL.revokeObjectURL(prevFrameUrlRef.current);
            }

            // Создаём Blob URL из бинарных данных (ArrayBuffer или Buffer)
            let frameUrl;
            if (data.frame instanceof ArrayBuffer || data.frame instanceof Uint8Array) {
                const blob = new Blob([data.frame], { type: 'image/jpeg' });
                frameUrl = URL.createObjectURL(blob);
            } else if (typeof data.frame === 'string') {
                // Fallback для base64 (обратная совместимость)
                frameUrl = data.frame;
            } else {
                // Buffer пришёл как объект с data
                const uint8Array = new Uint8Array(data.frame.data || data.frame);
                const blob = new Blob([uint8Array], { type: 'image/jpeg' });
                frameUrl = URL.createObjectURL(blob);
            }

            prevFrameUrlRef.current = frameUrl;
            setFrame(frameUrl);
            frameCountRef.current++;

            // Обновляем FPS раз в секунду
            const now = Date.now();
            if (now - lastFpsUpdateRef.current >= 1000) {
                setFps(frameCountRef.current);
                setFrameCount(prev => prev + frameCountRef.current);
                frameCountRef.current = 0;
                lastFpsUpdateRef.current = now;
            }
        }
    }, [streamId]);

    // Обработчик остановки стрима
    const handleStreamStopped = useCallback((data) => {
        if (data.streamId === streamId) {
            console.log('Стрим остановлен:', data.reason);
            setIsStreaming(false);
            setStreamId(null);
            setError(data.reason || 'Стрим остановлен');
        }
    }, [streamId]);

    // Подписываемся на события WebSocket
    useEffect(() => {
        webSocketService.on('stream:frame', handleFrame);
        webSocketService.on('stream:stopped', handleStreamStopped);

        return () => {
            webSocketService.off('stream:frame', handleFrame);
            webSocketService.off('stream:stopped', handleStreamStopped);
        };
    }, [handleFrame, handleStreamStopped]);

    // Останавливаем стрим при размонтировании
    useEffect(() => {
        return () => {
            if (streamId) {
                webSocketService.emit('stream:stop', { streamId });
            }
            // Очищаем Blob URL
            if (prevFrameUrlRef.current) {
                URL.revokeObjectURL(prevFrameUrlRef.current);
            }
        };
    }, [streamId]);

    // Запуск стрима
    const startStream = useCallback(() => {
        if (!selectedHost) {
            console.log('ComplexVideo: selectedHost не выбран');
            setError('Камера не выбрана');
            return;
        }

        console.log('ComplexVideo: Запуск стрима для', selectedHost);
        console.log('ComplexVideo: WebSocket подключен?', webSocketService.isConnected);

        // Проверяем подключение WebSocket
        if (!webSocketService.isConnected) {
            console.log('ComplexVideo: WebSocket не подключен, пытаемся подключиться...');
            webSocketService.connect();
            // Ждем подключения
            setTimeout(() => {
                if (webSocketService.isConnected) {
                    startStream();
                } else {
                    setError('WebSocket не подключен. Обновите страницу.');
                }
            }, 1000);
            return;
        }

        setLoading(true);
        setError(null);
        setFrame(null);
        setFrameCount(0);
        setFps(0);
        frameCountRef.current = 0;
        lastFpsUpdateRef.current = Date.now();

        let responseReceived = false;

        // Слушаем ответ от сервера
        const handleResponse = (data) => {
            console.log('ComplexVideo: Ответ на stream:start:', data);
            responseReceived = true;
            setLoading(false);

            if (data.success && data.data?.success) {
                setStreamId(data.data.streamId);
                setIsStreaming(true);
                setViewers(data.data.viewers || 1);
            } else {
                setError(data.data?.error || data.error || 'Не удалось запустить стрим');
            }

            webSocketService.off('response', handleResponse);
        };

        webSocketService.on('response', handleResponse);
        
        const payload = {
            type: selectedHost.type,
            ip: selectedHost.ip
        };
        console.log('ComplexVideo: Отправка stream:start', payload);
        webSocketService.emit('stream:start', payload);

        // Таймаут на случай если ответ не придет
        setTimeout(() => {
            if (!responseReceived) {
                console.log('ComplexVideo: Таймаут - ответ не получен');
                setLoading(false);
                setError('Таймаут подключения. Проверьте соединение с сервером.');
                webSocketService.off('response', handleResponse);
            }
        }, 15000);
    }, [selectedHost]);

    // Остановка стрима
    const stopStream = useCallback(() => {
        if (!streamId) return;

        webSocketService.emit('stream:stop', { streamId });
        setIsStreaming(false);
        setStreamId(null);
        setFps(0);
        
        // Очищаем Blob URL и кадр
        if (prevFrameUrlRef.current) {
            URL.revokeObjectURL(prevFrameUrlRef.current);
            prevFrameUrlRef.current = null;
        }
        setFrame(null);
    }, [streamId]);

    // Обработка колесика мыши (зум)
    const handleWheel = useCallback((e) => {
        if (!frame) return;
        e.preventDefault();

        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setTransform(prev => ({
            ...prev,
            scale: Math.min(5, Math.max(0.5, prev.scale * delta))
        }));
    }, [frame]);

    // Обработка перетаскивания
    const handleMouseDown = useCallback((e) => {
        if (!frame || transform.scale <= 1) return;
        e.preventDefault();
        setIsPanning(true);
        panStart.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
    }, [frame, transform]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isPanning) return;
            setTransform(prev => ({
                ...prev,
                x: e.clientX - panStart.current.x,
                y: e.clientY - panStart.current.y
            }));
        };

        const handleMouseUp = () => setIsPanning(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isPanning]);

    const resetTransform = useCallback(() => {
        setTransform({ x: 0, y: 0, scale: 1 });
    }, []);

    // Горячие клавиши
    useEffect(() => {
        const handleKeyDown = (e) => {
            switch (e.code) {
                case 'Space':
                    e.preventDefault();
                    if (isStreaming) {
                        stopStream();
                    } else {
                        startStream();
                    }
                    break;
                case 'Escape':
                    setFullscreen(false);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isStreaming, startStream, stopStream]);

    // Если нет камер
    // if (hostType !== 'crossroad' && hostType !== 'vgk') {
    //     return (
    //         <Box sx={{
    //             p: 4,
    //             textAlign: 'center',
    //             color: 'var(--text-muted)',
    //             backgroundColor: 'var(--background-default)',
    //             borderRadius: '12px',
    //             border: '1px solid var(--border-default)'
    //         }}>
    //             <VideocamOffIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
    //             <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
    //                 Просмотр видео недоступен
    //             </Typography>
    //             <Typography sx={{ fontSize: '0.85rem' }}>
    //                 Функция доступна только для перекрёстков и ВГК
    //             </Typography>
    //         </Box>
    //     );
    // }

    if (availableHosts.length === 0) {
        return (
            <Box sx={{
                p: 4,
                textAlign: 'center',
                color: 'var(--text-muted)',
                backgroundColor: 'var(--background-default)',
                borderRadius: '12px',
                border: '1px solid var(--border-default)'
            }}>
                <VideocamOffIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                    Камеры не найдены
                </Typography>
                <Typography sx={{ fontSize: '0.85rem' }}>
                    На этом комплексе нет доступных камер для просмотра
                </Typography>
            </Box>
        );
    }

    // Рендер видео
    const renderVideoViewer = (isFullscreen = false) => (
        <Box
            ref={!isFullscreen ? containerRef : null}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            sx={{
                flex: 1,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isFullscreen ? '#0d1117' : 'var(--background-default)',
                borderRadius: isFullscreen ? 0 : '8px',
                overflow: 'hidden',
                cursor: isPanning ? 'grabbing' : (frame && transform.scale > 1 ? 'grab' : 'default'),
                userSelect: 'none',
                position: 'relative',
                minHeight: isFullscreen ? 0 : { xs: 250, md: 350 },
                maxHeight: isFullscreen ? '100%' : 'none'
            }}
        >
            {loading ? (
                <Box sx={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    <CircularProgress size={{ xs: 28, md: 36 }} sx={{ color: 'var(--primary-color)' }} />
                    <Typography sx={{ mt: { xs: 1, md: 1.5 }, fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                        Подключение к камере...
                    </Typography>
                </Box>
            ) : error ? (
                <Box sx={{ textAlign: 'center', color: 'var(--error-color)' }}>
                    <ErrorOutlineIcon sx={{ fontSize: { xs: 36, md: 48 }, mb: { xs: 1, md: 1.5 }, opacity: 0.7 }} />
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, mb: 1 }}>
                        {error}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={startStream}
                        sx={{ fontSize: '0.75rem' }}
                    >
                        Повторить
                    </Button>
                </Box>
            ) : frame ? (
                <Box
                    sx={{
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                        transition: isPanning ? 'none' : 'transform 0.1s ease-out'
                    }}
                >
                    <img
                        src={frame}
                        alt="Видеопоток"
                        draggable={false}
                        onDoubleClick={resetTransform}
                        style={{
                            maxWidth: isFullscreen ? '95vw' : '100%',
                            maxHeight: isFullscreen ? '80vh' : '90%',
                            borderRadius: isFullscreen ? 0 : 8,
                            boxShadow: isFullscreen ? 'none' : '0 4px 24px rgba(0,0,0,0.2)'
                        }}
                    />
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    <VideocamIcon sx={{ fontSize: { xs: 36, md: 48 }, mb: { xs: 1, md: 1.5 }, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, mb: 2 }}>
                        {isStreaming ? 'Ожидание кадров...' : 'Нажмите "Старт" для просмотра'}
                    </Typography>
                </Box>
            )}
        </Box>
    );

    // Рендер панели управления
    const renderControls = (isFullscreen = false) => {
        const buttonStyle = {
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            border: `1px solid ${isDarkMode ? 'var(--border-default)' : 'var(--border-default)'}`,
            borderRadius: { xs: '4px', md: '6px' },
            color: isDarkMode ? 'var(--text-secondary)' : 'var(--text-secondary)',
            backgroundColor: isDarkMode ? 'var(--background-paper)' : 'transparent',
            '&:hover': {
                backgroundColor: isDarkMode ? 'var(--hover-bg)' : 'var(--hover-bg)',
                borderColor: 'var(--primary-color)'
            },
            '&:disabled': {
                opacity: 0.3,
                color: isDarkMode ? 'var(--text-muted)' : 'var(--text-muted)'
            }
        };

        const buttonStyleFullscreen = {
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: { xs: '4px', md: '6px' },
            color: 'rgba(255,255,255,0.9)',
            backgroundColor: 'rgba(255,255,255,0.05)',
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderColor: 'rgba(255,255,255,0.4)'
            },
            '&:disabled': { opacity: 0.3 }
        };

        const currentButtonStyle = isFullscreen ? buttonStyleFullscreen : buttonStyle;

        return (
            <Box sx={{
                p: { xs: 1, md: 1.5 },
                backgroundColor: isFullscreen ? 'rgba(13, 17, 23, 0.95)' : 'var(--background-subtle)',
                borderTop: `1px solid ${isFullscreen ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
                gap: 2,
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                {/* Статистика */}
                <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} alignItems="center">
                    {isStreaming && (
                        <>
                            <Chip
                                icon={<SignalCellularAltIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                                label={`${fps} FPS`}
                                size="small"
                                sx={{
                                    height: { xs: 20, md: 22 },
                                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                                    backgroundColor: alpha(isDarkMode ? '#3fb950' : '#1a7f37', 0.15),
                                    color: 'var(--success-color)',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                                }}
                            />
                            <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                                label={`${frameCount} кадров`}
                                size="small"
                                sx={{
                                    height: { xs: 20, md: 22 },
                                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                                    backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                                    color: 'var(--primary-color)',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                                }}
                            />
                            {viewers > 1 && (
                                <Chip
                                    icon={<PeopleIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                                    label={`${viewers} зрителей`}
                                    size="small"
                                    sx={{
                                        height: { xs: 20, md: 22 },
                                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                                        backgroundColor: alpha(isDarkMode ? '#d29922' : '#9a6700', 0.15),
                                        color: 'var(--warning-color)',
                                        '& .MuiChip-icon': { color: 'inherit' },
                                        '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                                    }}
                                />
                            )}
                        </>
                    )}
                </Stack>

                {/* Кнопки управления */}
                <Stack direction="row" spacing={{ xs: 0.75, md: 1 }} alignItems="center">
                    {!isStreaming ? (
                        <Tooltip title="Старт (Пробел)">
                            <IconButton
                                size="small"
                                onClick={startStream}
                                disabled={loading}
                                sx={{
                                    ...currentButtonStyle,
                                    color: 'var(--success-color)',
                                    borderColor: alpha(isDarkMode ? '#3fb950' : '#1a7f37', 0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(isDarkMode ? '#3fb950' : '#1a7f37', 0.15),
                                        borderColor: 'var(--success-color)'
                                    }
                                }}
                            >
                                <PlayArrowIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Стоп (Пробел)">
                            <IconButton
                                size="small"
                                onClick={stopStream}
                                sx={{
                                    ...currentButtonStyle,
                                    color: 'var(--error-color)',
                                    borderColor: alpha(isDarkMode ? '#f85149' : '#cf222e', 0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(isDarkMode ? '#f85149' : '#cf222e', 0.15),
                                        borderColor: 'var(--error-color)'
                                    }
                                }}
                            >
                                <StopIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Box sx={{ width: { xs: 8, md: 12 } }} />

                    <Tooltip title="Приблизить">
                        <IconButton
                            size="small"
                            onClick={() => setTransform(prev => ({
                                ...prev,
                                scale: Math.min(5, prev.scale * 1.2)
                            }))}
                            disabled={!frame}
                            sx={currentButtonStyle}
                        >
                            <ZoomInIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Отдалить">
                        <IconButton
                            size="small"
                            onClick={() => setTransform(prev => ({
                                ...prev,
                                scale: Math.max(0.5, prev.scale * 0.8)
                            }))}
                            disabled={!frame}
                            sx={currentButtonStyle}
                        >
                            <ZoomOutIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                        </IconButton>
                    </Tooltip>

                    {transform.scale !== 1 && (
                        <Tooltip title="Сбросить масштаб">
                            <IconButton
                                size="small"
                                onClick={resetTransform}
                                sx={{
                                    ...currentButtonStyle,
                                    color: 'var(--primary-color)',
                                    borderColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.15),
                                        borderColor: 'var(--primary-color)'
                                    }
                                }}
                            >
                                <ZoomOutMapIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {!isFullscreen ? (
                        <Tooltip title="Полный экран">
                            <IconButton
                                size="small"
                                onClick={() => setFullscreen(true)}
                                disabled={!frame}
                                sx={currentButtonStyle}
                            >
                                <FullscreenIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Закрыть">
                            <IconButton
                                size="small"
                                onClick={() => setFullscreen(false)}
                                sx={{
                                    ...buttonStyleFullscreen,
                                    borderColor: 'rgba(248, 81, 73, 0.5)',
                                    color: '#f85149',
                                    '&:hover': {
                                        backgroundColor: 'rgba(248, 81, 73, 0.2)',
                                        borderColor: '#f85149'
                                    }
                                }}
                            >
                                <CloseIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Box>
        );
    };

    const handleFullscreenClose = () => {
        setFullscreen(false);
        resetTransform();
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: 'var(--background-paper)',
            overflow: 'hidden',
            border: '1px solid var(--border-default)'
        }}>
            {/* Заголовок с выбором камеры */}
            {availableHosts.length > 0 && (
                <Box sx={{
                    p: { xs: 1, md: 1.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, md: 1.5 },
                    borderBottom: '1px solid var(--border-default)',
                    backgroundColor: 'var(--background-subtle)'
                }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, md: 0.75 }}>
                        <CameraAltIcon sx={{ fontSize: { xs: 16, md: 18 }, color: 'var(--primary-color)' }} />
                        <Typography sx={{
                            fontSize: { xs: '0.75rem', md: '0.8rem' },
                            fontWeight: 500,
                            color: 'var(--text-secondary)',
                            whiteSpace: 'nowrap'
                        }}>
                            Камера
                        </Typography>
                    </Stack>

                    <FormControl size="small" sx={{ minWidth: { xs: 120, md: 180 }, flex: 1, maxWidth: { xs: 200, md: 320 } }}>
                        <Select
                            value={selectedHost?.hostid || ''}
                            onChange={(e) => {
                                // Останавливаем текущий стрим при смене камеры
                                if (isStreaming) {
                                    stopStream();
                                }
                                const newHost = hosts.find(h => h.hostid === e.target.value);
                                setSelectedHost(newHost);
                                setFrame(null);
                                setError(null);
                            }}
                            displayEmpty
                            disabled={loading}
                            sx={{
                                backgroundColor: isDarkMode ? 'var(--background-paper)' : 'var(--background-paper)',
                                color: 'var(--text-primary)',
                                fontSize: { xs: '0.7rem', md: '0.75rem' },
                                height: { xs: 28, md: 32 },
                                borderRadius: { xs: '6px', md: '8px' },
                                '& .MuiSelect-select': { py: { xs: 0.4, md: 0.5 } },
                                '& .MuiSelect-icon': { color: 'var(--text-secondary)' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'var(--border-default)' : 'var(--border-default)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--primary-color)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--primary-color)'
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: isDarkMode ? '#161b22' : '#ffffff',
                                        backgroundImage: 'none',
                                        border: `1px solid ${isDarkMode ? '#30363d' : '#d0d7de'}`,
                                        borderRadius: { xs: '6px', md: '8px' },
                                        boxShadow: isDarkMode
                                            ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                                            : '0 8px 24px rgba(0, 0, 0, 0.15)',
                                        mt: 0.5,
                                        '& .MuiMenuItem-root': {
                                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                                            minHeight: { xs: 28, md: 32 },
                                            color: isDarkMode ? '#f0f6fc' : '#24292f',
                                            '&:hover': {
                                                backgroundColor: isDarkMode
                                                    ? 'rgba(177, 186, 196, 0.12)'
                                                    : 'rgba(208, 215, 222, 0.32)'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: isDarkMode
                                                    ? 'rgba(88, 166, 255, 0.15)'
                                                    : 'rgba(9, 105, 218, 0.08)',
                                                color: isDarkMode ? '#58a6ff' : '#0969da',
                                                '&:hover': {
                                                    backgroundColor: isDarkMode
                                                        ? 'rgba(88, 166, 255, 0.2)'
                                                        : 'rgba(9, 105, 218, 0.12)'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            {hosts.map(h => (
                                <MenuItem key={h.hostid} value={h.hostid}>
                                    {h.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedHost && (
                        <Typography
                            component="a"
                            href={
                                selectedHost.type === 'mjpeg245' ? `http://${selectedHost.ip}:24582/video/data.mjpg` :
                                selectedHost.type === 'mjpeg246' ? `http://${selectedHost.ip}:24682/video/data.mjpg` :
                                selectedHost.type === 'cyclop' ? `http://${selectedHost.ip}:18000/video/data.mjpg` :
                                `http://${selectedHost.ip}`
                            }
                            target="_blank"
                            sx={{
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                color: 'var(--primary-color)',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            Поток ↗
                        </Typography>
                    )}

                    {isStreaming && (
                        <Chip
                            label="LIVE"
                            size="small"
                            sx={{
                                height: { xs: 18, md: 20 },
                                fontSize: { xs: '0.6rem', md: '0.65rem' },
                                fontWeight: 700,
                                backgroundColor: 'var(--error-color)',
                                color: '#fff',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.7 }
                                }
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Область просмотра */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
                {renderVideoViewer()}
            </Box>

            {/* Управление */}
            <Box sx={{ flexShrink: 0 }}>
                {renderControls()}
            </Box>

            {/* Полноэкранный режим */}
            <Dialog
                open={fullscreen}
                onClose={handleFullscreenClose}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: '#0d1117',
                        m: 0,
                        maxHeight: '100vh',
                        height: '100vh',
                        borderRadius: 0,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    width: '100%',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        {renderVideoViewer(true)}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                        {renderControls(true)}
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default ComplexVideo;
